'use client';

import { usePathname } from 'next/navigation';
import Script from 'next/script';
import React, { useEffect, useRef } from 'react';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

declare global {
    interface Window {
        _paq?: unknown[][];
    }
}

function normalizeTrackerUrl(trackerUrl?: string): string | null {
    const trimmed = trackerUrl?.trim().replace(/\/+$/, '');
    // Charset check because the URL is interpolated into an inline script string.
    if (!trimmed || !trimmed.startsWith('https://') || !/^[\w\-.:/]+$/.test(trimmed)) {
        return null;
    }
    return trimmed;
}

/**
 * Loads Matomo in cookieless mode (setDoNotTrack + disableCookies, no consent banner integration
 * needed) when a tracker URL and site ID are configured for the cinema. Fires an additional
 * trackPageView on client-side route changes, since the base snippet's trackPageView call only
 * covers the initial page load.
 */
export default function MatomoTracker(): React.JSX.Element | null {
    const domainConfiguration = useDomainConfiguration();
    const matomo = domainConfiguration.websiteConfiguration?.matomo;
    const trackerUrl = normalizeTrackerUrl(matomo?.trackerUrl);
    const trimmedSiteId = matomo?.siteId?.trim();
    const siteId = trimmedSiteId && /^\d+$/.test(trimmedSiteId) ? trimmedSiteId : null;
    const isEnabled = !!trackerUrl && !!siteId;

    const pathname = usePathname();
    const isInitialPageviewRef = useRef(true);

    useEffect(() => {
        if (!isEnabled || typeof window === 'undefined' || !window._paq) {
            return;
        }

        // The base snippet below already fires the first trackPageView on load.
        if (isInitialPageviewRef.current) {
            isInitialPageviewRef.current = false;
            return;
        }

        window._paq.push(['setCustomUrl', window.location.href]);
        window._paq.push(['trackPageView']);
    }, [isEnabled, pathname]);

    if (!isEnabled) {
        return null;
    }

    const initScript = `
        var _paq = window._paq = window._paq || [];
        _paq.push(['setDoNotTrack', true]);
        _paq.push(['disableCookies']);
        _paq.push(['trackPageView']);
        _paq.push(['enableLinkTracking']);
        (function() {
            var u = '${trackerUrl}/';
            _paq.push(['setTrackerUrl', u + 'matomo.php']);
            _paq.push(['setSiteId', '${siteId}']);
            var d = document, g = d.createElement('script'), s = d.getElementsByTagName('script')[0];
            g.async = true;
            g.src = u + 'matomo.js';
            s.parentNode.insertBefore(g, s);
        })();
    `;

    return (
        <Script
            id="matomo-base"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{ __html: initScript }}
        />
    );
}
